import { type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  color?: "brand" | "emerald" | "amber" | "red" | "blue" | "purple";
  className?: string;
  badge?: string;
  onClick?: () => void;
}

const colorMap = {
  brand: {
    iconBg: "bg-brand-500/15",
    iconText: "text-brand-400",
    glow: "shadow-brand-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(99,102,241,0.3)]",
    borderHover: "hover:border-brand-500/30",
  },
  emerald: {
    iconBg: "bg-emerald-500/15",
    iconText: "text-emerald-400",
    glow: "shadow-emerald-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(34,197,94,0.3)]",
    borderHover: "hover:border-emerald-500/30",
  },
  amber: {
    iconBg: "bg-amber-500/15",
    iconText: "text-amber-400",
    glow: "shadow-amber-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]",
    borderHover: "hover:border-amber-500/30",
  },
  red: {
    iconBg: "bg-red-500/15",
    iconText: "text-red-400",
    glow: "shadow-red-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(239,68,68,0.3)]",
    borderHover: "hover:border-red-500/30",
  },
  blue: {
    iconBg: "bg-blue-500/15",
    iconText: "text-blue-400",
    glow: "shadow-blue-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(59,130,246,0.3)]",
    borderHover: "hover:border-blue-500/30",
  },
  purple: {
    iconBg: "bg-purple-500/15",
    iconText: "text-purple-400",
    glow: "shadow-purple-500/10",
    valueShadow: "drop-shadow-[0_0_8px_rgba(168,85,247,0.3)]",
    borderHover: "hover:border-purple-500/30",
  },
};

export function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = "brand",
  className,
  badge,
  onClick,
}: KPICardProps) {
  const colors = colorMap[color];
  const interactive = typeof onClick === "function";

  return (
    <div
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(
        "group relative rounded-xl border border-surface-700/50 bg-surface-800/80 backdrop-blur-sm p-5 transition-all duration-300 hover:shadow-lg",
        colors.glow,
        colors.borderHover,
        interactive &&
          "cursor-pointer hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60",
        className
      )}
    >
      {/* Subtle top-edge gradient glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-surface-600/50 to-transparent group-hover:via-brand-500/30 transition-all duration-500" />

      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-surface-400">{title}</p>
            {badge && (
              <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                {badge}
              </span>
            )}
          </div>
          <p
            className={cn(
              "text-3xl font-bold text-surface-100 transition-all duration-300",
              colors.valueShadow
            )}
          >
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-surface-500">{subtitle}</p>
          )}
        </div>
        <div
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-110",
            colors.iconBg
          )}
        >
          <div className={colors.iconText}>{icon}</div>
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1.5">
          {trend.value > 0 ? (
            <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
          ) : trend.value < 0 ? (
            <TrendingDown className="h-3.5 w-3.5 text-red-400" />
          ) : (
            <Minus className="h-3.5 w-3.5 text-surface-400" />
          )}
          <span
            className={cn(
              "text-xs font-medium",
              trend.value > 0
                ? "text-emerald-400"
                : trend.value < 0
                  ? "text-red-400"
                  : "text-surface-400"
            )}
          >
            {trend.value > 0 ? "+" : ""}
            {trend.value}%
          </span>
          <span className="text-xs text-surface-500">{trend.label}</span>
        </div>
      )}
    </div>
  );
}
